/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"

import type { OrganizationDetail } from "../../features/organization/organization-types"
import { OrganizationViewModel } from "../../features/organization/organization-viewmodel"
import { useViewModel } from "../app/app-hooks"
import { ClipboardValue } from "../components/clipboard-value"
import { Card, CardContent, CardHeader, CardTitle } from "../primitives/card"
import { Field, FieldLabel } from "../primitives/field"
import { NativeSelect, NativeSelectOption } from "../primitives/native-select"

// Use an ID that cannot represent a real organization as the "no selection" value.
const NO_ORG_ID = -1

export const ArtifactoryCredentials = observer(() => {
    const [orgId, setOrgId] = useState(NO_ORG_ID)
    const [org, setOrg] = useState<OrganizationDetail | null>(null)

    const vm = useViewModel(OrganizationViewModel)

    // Pick the first organization once the list loads, but never overwrite a
    // selection the user has already made when the list is refreshed.
    useEffect(() => {
        if (vm.hasOrganizations && orgId === NO_ORG_ID) {
            setOrgId(vm.organizations[0].id)
        }
    }, [orgId, vm.organizations, vm.hasOrganizations])

    // Credentials belong to the selected organization. Clear the old details
    // immediately and ignore a response for a selection that is no longer active.
    useEffect(() => {
        let cancelled = false
        setOrg(null)

        void (async () => {
            if (orgId === NO_ORG_ID) return

            const result = await vm.getOrganization(orgId)
            if (cancelled) return
            setOrg(result.success ? result.data : null)
        })()

        return () => {
            cancelled = true
        }
    }, [orgId, vm])

    return (
        <Card className="gap-[calc(var(--card-spacing)*0.5)]">
            <CardHeader>
                <CardTitle>
                    <h2 className="m-0 text-xl font-bold uppercase">Artifactory Credentials</h2>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <p className="m-0">Use our Artifactory repository to obtain the latest version of our SDKs.</p>

                {vm.hasMultipleOrganizations && (
                    <Field>
                        <FieldLabel htmlFor="orgSelect">Organization:</FieldLabel>
                        <NativeSelect
                            id="orgSelect"
                            value={orgId}
                            onChange={(evt) => setOrgId(Number(evt.target.value))}
                        >
                            {vm.organizations.map((organization) => (
                                <NativeSelectOption key={organization.id} value={organization.id}>
                                    {organization.name}
                                </NativeSelectOption>
                            ))}
                        </NativeSelect>
                    </Field>
                )}

                <dl className="grid grid-cols-[max-content_minmax(0,1fr)] items-center p-3 gap-x-2 gap-y-1 border bg-muted m-0">
                    <dt className="font-bold">Artifactory Base URL:</dt>
                    <dd className="m-0 font-mono">
                        <ClipboardValue value={org?.artifactoryUrl} />
                    </dd>

                    <dt className="font-bold">Username:</dt>
                    <dd className="m-0 font-mono">
                        <ClipboardValue value={org?.artifactoryUsername} />
                    </dd>

                    <dt className="font-bold">Password:</dt>
                    <dd className="m-0 font-mono">
                        <ClipboardValue value={org?.artifactoryPassword} masked />
                    </dd>
                </dl>
            </CardContent>
        </Card>
    )
})
